import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@verlo/shared/components/ui/button';
import { Input } from '@verlo/shared/components/ui/input';
import { Switch } from '@verlo/shared/components/ui/switch';
import { Notice } from '@verlo/shared/components/ui/notice';
import { OptionChips } from '@verlo/shared/components/ui/segmented';
import { beforeServiceApi } from '@verlo/shared/services/api';
import ReportsLayout from '../../../components/reports/ReportsLayout';
import { nextSend, sendWords } from '../../../lib/nextSend';
import { TimeField } from '@verlo/shared/components/ui/time-field';

// Before Service: the email a manager reads before the day starts (backend/before_service.py).
// Each person sets up their own: which restaurants, when, what it's compared with, and which
// sections. The preview beside the settings is the email itself, rebuilt from yesterday's
// figures as they change things, which is why there is no "send me one now".
//
// Mocked and agreed 12 Oct 2026 ("Before Service, Mocked").

const DAYS = [
  { value: 0, label: 'Mon' }, { value: 1, label: 'Tue' }, { value: 2, label: 'Wed' },
  { value: 3, label: 'Thu' }, { value: 4, label: 'Fri' }, { value: 5, label: 'Sat' }, { value: 6, label: 'Sun' },
];

const SECTIONS = [
  { key: 'insight', label: 'Two lines from Verlo Insight' },
  { key: 'yesterday', label: 'Yesterday’s sales, covers and spend' },
  { key: 'week', label: 'The week so far' },
  { key: 'worth_a_look', label: 'Worth a look' },
  { key: 'sellers', label: 'Best sellers and quiet dishes' },
  { key: 'tonight', label: 'Tonight’s bookings' },
];

const same = (a, b) => JSON.stringify(a) === JSON.stringify(b);

function Row({ label, children }) {
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-2 border-t border-hairline py-2.5 first:border-t-0">
      <span className="w-32 shrink-0 text-[13px] text-ink-soft">{label}</span>
      <div className="min-w-0 flex-1">{children}</div>
    </div>
  );
}

function Card({ title, hint, children }) {
  return (
    <section className="border border-hairline bg-white px-5 py-4">
      <h2 className="font-heading text-[15px] font-extrabold text-ink">{title}</h2>
      {hint && <p className="mb-2 mt-0.5 text-[12.5px] text-ink-soft">{hint}</p>}
      {children}
    </section>
  );
}

export default function BeforeServicePage() {
  const [data, setData] = useState(null);          // what the server says: settings, sites, shows
  const [form, setForm] = useState(null);          // the settings on screen
  const [saving, setSaving] = useState(false);
  const [showThresholds, setShowThresholds] = useState(false);
  const [preview, setPreview] = useState({ loading: true, html: '', subject: '', error: null });
  const previewSeq = useRef(0);

  useEffect(() => {
    beforeServiceApi.get()
      .then(({ data: d }) => { setData(d); setForm(d.settings); })
      .catch((e) => toast.error(e?.response?.data?.detail || 'Couldn’t load Before Service'));
  }, []);

  // The preview follows the form, a moment after the last change.
  useEffect(() => {
    if (!form || !form.site_ids?.length) return undefined;
    const seq = ++previewSeq.current;
    setPreview((p) => ({ ...p, loading: true }));
    const t = setTimeout(() => {
      beforeServiceApi.preview({ ...form, enabled: true })
        .then(({ data: d }) => { if (seq === previewSeq.current) setPreview({ loading: false, html: d.html, subject: d.subject, error: null }); })
        .catch((e) => { if (seq === previewSeq.current) setPreview({ loading: false, html: '', subject: '', error: e?.response?.data?.detail || 'The preview couldn’t be built' }); });
    }, 400);
    return () => clearTimeout(t);
  }, [form]);

  const set = useCallback((patch) => setForm((f) => ({ ...f, ...patch })), []);
  const setSection = (key, on) => setForm((f) => ({ ...f, sections: { ...f.sections, [key]: on } }));
  const setThreshold = (key, value) => setForm((f) => ({ ...f, thresholds: { ...f.thresholds, [key]: value } }));

  // Running is "emails are going out": set up by this person, or on by default for a general
  // manager who hasn't touched it. Reading only `set_up` hid the "It's on for you" notice and
  // the stop link from exactly the people it was on for, and offered to start what had started.
  const setUp = data?.set_up;
  const running = (setUp || data?.on_by_default) && data?.settings?.enabled;
  const dirty = useMemo(() => data && form && !same({ ...form, enabled: true }, { ...data.settings, enabled: true }), [data, form]);

  // `switchOnly`: the header switch turns it on or off as saved, and leaves edits below as they are.
  const save = async (enabled = true, switchOnly = false) => {
    setSaving(true);
    try {
      const { data: d } = await beforeServiceApi.save({ ...(switchOnly ? data.settings : form), enabled });
      setData((prev) => ({ ...prev, settings: d.settings, set_up: true, on_by_default: false }));
      setForm(switchOnly ? (f) => ({ ...f, enabled: d.settings.enabled }) : d.settings);
      toast.success(enabled
        ? (running ? 'Your emails are updated' : `On. The first one arrives ${sendWords(nextSend(d.settings.days, d.settings.time_hhmm))}`)
        : (running ? 'Off. Nothing more is sent' : 'Saved. Nothing is sent until you switch it on'));
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Couldn’t save that');
    } finally {
      setSaving(false);
    }
  };

  if (!form) {
    return <ReportsLayout><div className="h-40 animate-pulse border border-hairline bg-surface-2" /></ReportsLayout>;
  }

  const sites = data.sites || [];
  const many = sites.length > 1;
  const shows = data.shows || {};
  const lookIncludes = ['refunds well above a normal day', shows.cash && 'a till that didn’t balance', shows.waste && 'waste up sharply']
    .filter(Boolean).join(', ');

  // The switch at the top starts and stops it (Back office sweep, Oct 2026); the button at the
  // bottom only appears when there's a change to save.
  const canSend = form.site_ids.length > 0 && form.days.length > 0;
  // When it next goes, as saved: edits below don't move it until they're saved.
  const next = nextSend(data.settings.days, data.settings.time_hhmm);

  return (
    <ReportsLayout>
      <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_380px]" data-testid="before-service-page">
        <div className="space-y-3">
          <Card title="Before Service" hint="An email each morning with how yesterday went, what needs a look, and tonight’s bookings.">
            <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1.5">
              <label className="flex items-center gap-2.5 text-[13.5px] font-semibold text-ink">
                <Switch
                  checked={!!running}
                  disabled={saving || (!running && !(data.settings.site_ids?.length && data.settings.days?.length))}
                  onCheckedChange={(on) => save(on, true)}
                  data-testid="bs-enabled"
                />
                Send the Before Service email
              </label>
              <span className="text-[13px] text-ink-soft" data-testid="bs-next">
                {running
                  ? (next ? `Next one: ${sendWords(next)}, to ${data.email || 'you'}` : 'Pick a day for it to go')
                  : 'Off: nothing is sent'}
              </span>
            </div>
            {data.on_by_default && running && (
              <Notice tone="info" word="On" className="mt-2">
                It’s on for you as general manager. Change anything below, or stop it.
              </Notice>
            )}
          </Card>

          <Card title="Your restaurants, and when" hint="Pick the restaurants you want to hear about. You’ll get one email for each, at the time you choose.">
            <Row label="Restaurants">
              <OptionChips
                multiple
                value={form.site_ids}
                onChange={(v) => set({ site_ids: v, combined: v.length > 1 ? form.combined : false })}
                options={sites.map((s) => ({ value: s.id, label: s.name, testId: `bs-site-${s.id}` }))}
              />
              {many && form.site_ids.length > 1 && (
                <label className="mt-2 flex items-center gap-2 text-[13px] text-ink-soft">
                  <Switch checked={form.combined} onCheckedChange={(v) => set({ combined: v })} data-testid="bs-combined" />
                  All of them in one email
                </label>
              )}
            </Row>
            <Row label="Time">
              <TimeField value={form.time_hhmm} onChange={(t) => set({ time_hhmm: t })} aria-label="Time" testId="bs-time" />
            </Row>
            <Row label="Days">
              <OptionChips multiple value={form.days} onChange={(v) => set({ days: [...v].sort((a, b) => a - b) })} options={DAYS} />
            </Row>
            <Row label="Compare with">
              <OptionChips
                value={form.compare}
                onChange={(v) => set({ compare: v })}
                options={[{ value: 'last_week', label: 'The same day last week' }, { value: 'avg4', label: 'The average of the last 4' }]}
              />
            </Row>
            <Row label="Subject line">
              <label className="flex items-center gap-2 text-[13px]">
                <Switch checked={form.subject_figures} onCheckedChange={(v) => set({ subject_figures: v })} data-testid="bs-subject-figures" />
                <span>Show sales in the subject line <span className="text-ink-faint">· off, so they don’t show on a locked phone</span></span>
              </label>
            </Row>
          </Card>

          <Card title="What’s in it" hint="Turn off anything you don’t read. The email gets shorter, not emptier.">
            {SECTIONS.map((s) => (
              <div key={s.key} className="flex items-center gap-3 border-t border-hairline py-2 first:border-t-0">
                <Switch checked={!!form.sections?.[s.key]} onCheckedChange={(v) => setSection(s.key, v)} data-testid={`bs-section-${s.key}`} />
                <span className="flex-1 text-[13.5px]">
                  {s.label}
                  {s.key === 'worth_a_look' && <span className="text-ink-faint"> · {lookIncludes}</span>}
                </span>
                {s.key === 'worth_a_look' && (
                  <button type="button" className="text-[12.5px] font-semibold text-verlo-hover" onClick={() => setShowThresholds((x) => !x)}>
                    {showThresholds ? 'Done' : 'Change'}
                  </button>
                )}
              </div>
            ))}
            {showThresholds && (
              <div className="mt-1 grid gap-2 border-t border-hairline pt-3 text-[13px] sm:grid-cols-3" data-testid="bs-thresholds">
                <label className="flex items-center gap-2">Refunds over
                  <Input type="number" min="1" step="0.5" className="h-8 w-16" value={form.thresholds.refund_ratio} onChange={(e) => setThreshold('refund_ratio', Number(e.target.value))} />× normal
                </label>
                {shows.cash && (
                  <label className="flex items-center gap-2">A till out by £
                    <Input type="number" min="0" className="h-8 w-16" value={form.thresholds.cash_difference} onChange={(e) => setThreshold('cash_difference', Number(e.target.value))} />
                  </label>
                )}
                {shows.waste && (
                  <label className="flex items-center gap-2">Waste up
                    <Input type="number" min="0" className="h-8 w-16" value={form.thresholds.waste_pct} onChange={(e) => setThreshold('waste_pct', Number(e.target.value))} />%
                  </label>
                )}
              </div>
            )}
          </Card>

          {dirty && (
            <div className="flex flex-wrap items-center gap-3">
              <Button className="bg-verlo hover:bg-verlo-hover" disabled={saving || !canSend} onClick={() => save(!!running)} data-testid="bs-save">
                {running ? 'Update my emails' : 'Save changes'}
              </Button>
              <Button variant="ghost" disabled={saving} onClick={() => setForm(data.settings)}>Undo changes</Button>
            </div>
          )}
        </div>

        <aside>
          <div className="mb-1.5 text-[11px] font-bold uppercase tracking-[0.06em] text-ink-faint">Preview · tomorrow’s, with yesterday’s figures</div>
          {preview.subject && (
            <div className="mb-2 border border-hairline bg-white px-3 py-2 text-[12.5px]" data-testid="bs-preview-subject">
              <span className="text-ink-faint">Subject </span>{preview.subject}
            </div>
          )}
          <div className={`h-[720px] overflow-hidden border border-hairline-strong bg-paper ${preview.loading ? 'opacity-60' : ''}`}>
            {preview.error ? (
              <p className="p-4 text-[13px] text-ink-soft">{preview.error}</p>
            ) : (
              <iframe title="Before Service preview" srcDoc={preview.html} className="h-full w-full" sandbox="" data-testid="bs-preview" />
            )}
          </div>
        </aside>
      </div>
    </ReportsLayout>
  );
}
